// Google Analytics 4 integration.
//
// Mirrors Google's standard inline snippet shape — the variant that uses
// rest-params `(...args)` and a `send_page_view: false` initial config
// loaded and parsed in production but never transmitted /collect
// requests, even though gtag.js's enhanced-measurement (gtm.dom,
// gtm.scrollDepth, …) ran fine. Matching the official snippet exactly
// (Arguments-shaped queue push + default config) restored transmission.
//
// Hash-router SPA note: the default config now fires the initial
// page_view itself; we still listen to `hashchange` and fire an
// additional `page_view` event on every in-app navigation so deep
// links and hash navigations get attributed.
//
// Local dev: tracking is gated to PROD builds (`import.meta.env.PROD`)
// so `npm run dev` doesn't pollute the live property with developer
// traffic.

const MEASUREMENT_ID = 'G-RNT7W754XM';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let initialized = false;

function isEnabled(): boolean {
  return typeof window !== 'undefined' && import.meta.env.PROD && MEASUREMENT_ID.startsWith('G-');
}

function fireSpaPageView(): void {
  window.gtag?.('event', 'page_view', {
    page_location: window.location.href,
    page_title: document.title,
  });
}

export function initAnalytics(): void {
  if (initialized || !isEnabled()) return;
  initialized = true;

  // Stub gtag BEFORE injecting the script — matches Google's snippet
  // ordering. The stub pushes `arguments` (not a rest-params array);
  // gtag.js's queue processor expects the Arguments shape and silently
  // drops transmission for entries pushed as plain arrays.
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  } as Window['gtag'];
  window.gtag!('js', new Date());
  // Default config — auto-fires the initial page_view. Do NOT pass
  // `send_page_view: false` here; in production that suppressed every
  // outbound /collect request despite the stub queue looking correct.
  window.gtag!('config', MEASUREMENT_ID);

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // SPA hash-router navigations fire an additional page_view per change.
  // The initial page_view was already fired by the config call above.
  window.addEventListener('hashchange', fireSpaPageView);
}

/** Fire a GA4 event. No-op when analytics is disabled. */
export function gtagEvent(eventName: string, params?: Record<string, unknown>): void {
  window.gtag?.('event', eventName, params ?? {});
}

/**
 * Set GA4 user properties — survives across events for the rest of the
 * session. Use to attach a `user_type` ('anon' / 'authenticated' /
 * 'first_customer' / 'repeat_customer' / 'admin') so reports can
 * segment by audience without a server-side join.
 */
export function gtagSetUserProperties(props: Record<string, unknown>): void {
  window.gtag?.('set', 'user_properties', props);
}

/**
 * Set a stable user_id on GA. Lets cross-device sessions stitch
 * together. Uses the `set` form (not a re-config) so it doesn't
 * trigger a duplicate auto page_view.
 */
export function gtagSetUserId(userId: string | null): void {
  window.gtag?.('set', { user_id: userId });
}
